import { ThemeEvents } from '@theme/events';
import { parseTiers, TierFormatter, renderTierCards, tierFor } from '@theme/wavetag-tiers';

/**
 * Volume discount cards ("1 kus", "2+ kusy", "5+ kusů") for ordinary product pages.
 *
 * The cards are synced with the theme's own quantity selector: clicking a card sets the quantity and changing the
 * quantity highlights the matching card. They only DISPLAY the discount; the real discount has to exist in Shopify
 * (automatic discounts) with the same quantities and percentages.
 */
class WavetagVolumeTiers extends HTMLElement {
  /** @type {import('@theme/wavetag-tiers').Tier[]} */
  #tiers = [];
  /** @type {TierFormatter | undefined} */
  #fmt;
  #price = 0;
  /** @type {Record<string, number>} */
  #prices = {};
  /** Formatted unit price per variant per tier, rendered by Liquid. @type {Record<string, Record<string, string>>} */
  #tierPrices = {};
  /** @type {string} */
  #variantId = '';
  /** @type {any} */
  #selector = null;
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.#fmt = new TierFormatter(this.dataset.locale || 'en', this.dataset.currency || 'CZK', 'item');
    this.#tiers = parseTiers(this.dataset.tiers, Number(this.dataset.max) || 99);
    this.#price = Number(this.dataset.price) || 0;

    try {
      this.#prices = JSON.parse(this.dataset.prices || '{}');
    } catch {
      this.#prices = {};
    }

    try {
      this.#tierPrices = JSON.parse(this.dataset.tierPrices || '{}');
    } catch {
      this.#tierPrices = {};
    }

    this.#variantId = Object.keys(this.#tierPrices).find((id) => this.#prices[id] === this.#price) || '';

    if (this.#tiers.length === 0) return;

    const title = this.#fmt.text('title');
    this.innerHTML = `
      <div class="wt-tiers">
        <p class="wt-tiers__title">${title.replace(/[&<>]/g, '')}</p>
        <div class="wt-tiers__list" role="radiogroup" aria-label="${title.replace(/["&<>]/g, '')}"></div>
      </div>`;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#render(1);

    customElements.whenDefined('quantity-selector-component').then(() => {
      this.#selector = this.#findSelector();
      this.#render(this.#currentQuantity());
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @returns {any} */
  #findSelector() {
    const scope = this.closest('.shopify-section') || document;
    return (
      scope.querySelector('product-form-component quantity-selector-component') ||
      scope.querySelector('quantity-selector-component')
    );
  }

  /** @returns {number} */
  #currentQuantity() {
    const value = Number(this.#selector?.getValue?.());
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @param {number} count */
  #render(count) {
    const list = this.querySelector('.wt-tiers__list');
    if (!list || !this.#fmt) return;

    list.innerHTML = renderTierCards({
      tiers: this.#tiers,
      count,
      unitPrice: this.#price,
      fmt: this.#fmt,
    });

    this.#renderHeadlinePrice(count);
  }

  /**
   * Puts the unit price of the tier the customer has reached into the price at the top of
   * the page (and the sticky bar), so the headline figure matches the card they picked.
   * Strings come pre-formatted from Liquid; anything missing leaves the price untouched.
   * @param {number} count
   */
  #renderHeadlinePrice(count) {
    const prices = this.#tierPrices[this.#variantId];
    if (!prices) return;

    const tier = tierFor(this.#tiers, count);
    const formatted = prices[String(tier.qty)] ?? prices['1'];
    if (!formatted) return;

    const scope = this.closest('.shopify-section');
    if (!scope) return;

    for (const container of scope.querySelectorAll('[ref="priceContainer"]')) {
      const price = container.querySelector('.price');
      if (price && price.textContent !== formatted) price.textContent = formatted;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('[data-tier]');
    if (!(card instanceof HTMLElement)) return;

    this.#selector ||= this.#findSelector();
    const quantity = Number(card.dataset.tier);
    if (!this.#selector || !quantity) return;

    // Same calls the theme's own plus / minus buttons make, so price per item and the add to cart form stay in sync.
    this.#selector.setValue(String(quantity));
    this.#selector.onQuantityChange?.();
    this.#selector.updateButtonStates?.();
    this.#render(quantity);
  };

  /** @param {Event} event */
  #onQuantityUpdate = (event) => {
    if (this.#selector && event.target !== this.#selector) return;
    this.#render(this.#currentQuantity());
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const id = /** @type {any} */ (event).detail?.resource?.id;
    const price = id != null ? this.#prices[String(id)] : undefined;
    if (price == null) return;

    this.#price = price;
    this.#variantId = String(id);
    this.#render(this.#currentQuantity());
  };
}

if (!customElements.get('wavetag-volume-tiers')) {
  customElements.define('wavetag-volume-tiers', WavetagVolumeTiers);
}
